import { createFileRoute } from '@tanstack/react-router';
import { CreditCard, Zap } from 'lucide-react';
import { Badge } from '@/components/atoms/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/atoms/card';
import { Skeleton } from '@/components/atoms/skeleton';
import { DetailPageLayout } from '@/components/layouts';
import { Fullbleed } from '@/contexts/fullbleed';
import { useBilling } from '@/hooks/settings/useBilling';
import { useCompanyTimezone } from '@/hooks/settings/useCompanyTimezone';
import { formatDateTimeInTimeZone } from '@/lib/date-values';
import type { PlanTier, UsageMeter } from '@/types/settings';

/**
 * Read-only subscription overview. Plan management can be wired once the
 * tenant subscription backend exists.
 */
export const Route = createFileRoute('/_dashboard/dashboard/settings/subscription')({
  component: SubscriptionSettingsPage,
});

// ─── Constants ────────────────────────────────────────────────────────────────

const PLAN_ORDER: PlanTier[] = ['starter', 'growth', 'professional', 'enterprise'];

const PLAN_LABELS: Record<PlanTier, string> = {
  starter: 'Starter',
  growth: 'Growth',
  professional: 'Professional',
  enterprise: 'Enterprise',
};

const PLAN_COLORS: Record<PlanTier, string> = {
  starter: 'bg-slate-100 text-slate-700',
  growth: 'bg-blue-100 text-blue-700',
  professional: 'bg-purple-100 text-purple-700',
  enterprise: 'bg-amber-100 text-amber-700',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function usagePercent(meter: UsageMeter): number {
  if (meter.limit === null) return 0;
  return Math.min(Math.round((meter.used / meter.limit) * 100), 100);
}

function formatUsage(meter: UsageMeter): string {
  const used = meter.used.toLocaleString();
  const limit = meter.limit === null ? '∞' : meter.limit.toLocaleString();
  return `${used} / ${limit} ${meter.unit}`;
}

function isPlanTier(value: unknown): value is PlanTier {
  return typeof value === 'string' && PLAN_ORDER.includes(value as PlanTier);
}

function formatPlanLabel(plan: unknown, label?: string | null): string {
  if (label) return label;
  return isPlanTier(plan) ? PLAN_LABELS[plan] : 'No plan';
}

function formatBillingDate(value?: string | null, timeZone?: string | null): string {
  if (!value) return 'Not scheduled';
  const formatted = formatDateTimeInTimeZone(value, timeZone, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  return formatted || 'Not scheduled';
}

function formatBillingAmount(amount?: number | null, currency?: string | null): string {
  if (typeof amount !== 'number') return 'Not set';
  const symbol = currency === 'usd' || !currency ? '$' : currency;
  return `${symbol}${amount.toFixed(2)}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

function SubscriptionSettingsPage() {
  const { data: billing, isLoading } = useBilling();
  const timeZone = useCompanyTimezone();

  if (isLoading) {
    return (
      <>
        <Fullbleed />
        <DetailPageLayout
          header={
            <div className="min-w-0">
              <h1 className="truncate text-base font-semibold">Subscription</h1>
              <p className="text-xs text-muted-foreground">
                View your current Notifi plan and usage.
              </p>
            </div>
          }
          contentClassName="p-4"
        >
          <div className="space-y-5">
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        </DetailPageLayout>
      </>
    );
  }

  const currentPlan = isPlanTier(billing?.plan) ? billing.plan : null;
  const usage = billing?.usage ?? [];

  return (
    <>
      <Fullbleed />
      <DetailPageLayout
        header={
          <div className="min-w-0">
            <h1 className="truncate text-base font-semibold">Subscription</h1>
            <p className="text-xs text-muted-foreground">
              View your current Notifi plan and usage.
            </p>
          </div>
        }
        contentClassName="p-4"
      >
        <div className="space-y-5">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <CreditCard className="h-4 w-4" />
                Current Plan
              </CardTitle>
            </CardHeader>
            <CardContent>
              {billing ? (
                <div className="grid gap-5 md:grid-cols-[minmax(0,1fr)_220px_220px]">
                  <div className="min-w-0">
                    <p className="text-sm text-muted-foreground">Plan</p>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <span className="break-words text-2xl font-semibold capitalize">
                        {formatPlanLabel(billing.plan, billing.planLabel)}
                      </span>
                      <span
                        className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          currentPlan ? PLAN_COLORS[currentPlan] : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {billing.billingCycle ?? 'No cycle'}
                      </span>
                      {billing.cancelAtPeriodEnd && (
                        <Badge variant="outline" className="text-xs">
                          Cancels at period end
                        </Badge>
                      )}
                    </div>
                  </div>

                  <ReadOnlyMetric
                    label="Next billing"
                    value={formatBillingDate(billing.nextBillingDate, timeZone)}
                  />
                  <ReadOnlyMetric
                    label="Amount"
                    value={formatBillingAmount(billing.amount, billing.currency)}
                  />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No billing information available.</p>
              )}
            </CardContent>
          </Card>

          {usage.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Zap className="h-4 w-4" />
                  Usage
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                {usage.map((meter) => {
                  const pct = usagePercent(meter);
                  const isUnlimited = meter.limit === null;

                  return (
                    <div key={meter.label} className="space-y-1.5">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium">{meter.label}</span>
                        <span className="text-muted-foreground">{formatUsage(meter)}</span>
                      </div>
                      {!isUnlimited && (
                        <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                          <div
                            className={`h-full rounded-full transition-all ${pct >= 90 ? 'bg-destructive' : 'bg-primary'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      )}
                      {isUnlimited && <p className="text-xs text-muted-foreground">Unlimited</p>}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}
        </div>
      </DetailPageLayout>
    </>
  );
}

function ReadOnlyMetric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="mt-1 break-words text-sm font-medium">{value}</p>
    </div>
  );
}
